<template>
  <div class="d-flex flex-column gap-3">
    <div class="d-flex justify-content-between align-items-center p-2 bg-light rounded border">
      <div>
        <h5 class="mb-0">Użytkownicy</h5>
        <small class="text-muted">Zarządzanie rolami użytkowników</small>
      </div>
      <div v-if="canManageUsersAccess">
        <button class="btn btn-primary btn-sm" @click="showCreateUserModal = true">
          <i class="fas fa-user-plus me-1"></i> Dodaj użytkownika
        </button>
      </div>
    </div>

    <div v-if="!canViewUsersAccess" class="alert alert-warning mb-0">
      Brak uprawnień do przeglądania użytkowników.
    </div>

    <CommonTable2
      v-else
      :key="tableConfig.data"
      :table="tableConfig"
      rowIdentifier="id"
      :currentPage="currentPage"
      :totalRows="totalRows"
      :totalPages="totalPages"
      :showPaginationBottom="true"
      :showPaginationTop="false"
      :showMassActions="canManageUsersAccess"
      :multiselect="true"
      :showRowActions="true"
      @onPageChange="handlePageChange"
      @onSort="handleSort"
      @onItemAction="handleItemAction"
      @onMassAction="handleMassAction"
      :per-page="pageSize"
    />

    <UserRolesModal
      v-if="selectedUser"
      v-model:showModal="showRolesModal"
      :user="selectedUser"
      :rolesCatalog="rolesCatalog"
      @rolesUpdated="handleRolesUpdated"
    />

    <CreateUserModal
      v-model:showModal="showCreateUserModal"
      :rolesCatalog="rolesCatalog"
      @onUserCreated="handleUserCreated"
    />
  </div>
</template>

<script>
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import UserRolesModal from "@/views/UserCommonManager/components/UserRolesModal.vue";
import CreateUserModal from "@/views/UserCommonManager/components/CreateUserModal.vue";
import { useUserRbacManagerModule } from "@/state/pinia/userRbacManagerModule";
import { useUserProfileModule } from "@/state/pinia/userProfileModule";
import Swal from "sweetalert2";
import {
  canManageUsers,
  canViewUsers,
  normalizeRoleName,
  normalizeRolesPayload,
} from "@/helpers/rbacAccess";

export default {
  name: "UsersListView",
  components: { CommonTable2, UserRolesModal, CreateUserModal },
  data() {
    return {
      currentPage: 1,
      totalRows: 0,
      totalPages: 1,
      pageSize:20,
      sortColumn: "id",
      sortingDirection: "ASC",
      users: [],
      userRolesMap: {},
      rolesCatalog: [],
      selectedUser: null,
      showRolesModal: false,
      showCreateUserModal: false,
      tableConfig: {
        headers: [
          { key: "login", text: "Login", type: "text", sortable: true, width: "180px" },
          { key: "email", text: "Email", type: "text", sortable: true, width: "220px" },
          { key: "displayName", text: "Nazwa", type: "text", sortable: true, width: "220px" },
          { key: "roles", text: "Role", type: "text", sortable: false, width: "260px" },
          { key: "actions", text: "Akcje", type: "actions", sortable: false, width: "120px" },
        ],
        data: [],
        massActions: [
          {
            action: "delete-users",
            text: "Usuń użytkowników",
            icon: "fas fa-trash-alt",
            class: "btn btn-danger mass-operations-btn",
            enabled: true,
          },
        ],
      },
    };
  },
  computed: {
    rbacModule() {
      return useUserRbacManagerModule();
    },
    profileModule() {
      return useUserProfileModule();
    },
    canViewUsersAccess() {
      return canViewUsers();
    },
    canManageUsersAccess() {
      return canManageUsers();
    },
  },
  methods: {
    handlePageChange(page) {
      this.currentPage = page;
      this.refreshUsersList();
    },
    handleSort(sortingKeyValue) {
      this.sortColumn = sortingKeyValue;
      this.currentPage = 1;
      this.refreshUsersList();
    },
    handleItemAction(event) {
      if (event.action.action === "manage-roles") {
        this.selectedUser = event.itemData;
        this.openRolesModal();
      } else if (event.action.action === "view-profile") {
        this.viewUserProfile(event.itemData);
      } else if (event.action.action === "delete-user") {
        this.confirmDeleteUser(event.itemData);
      }
    },
    handleMassAction(event) {
      if (event.action === "delete-users") {
        this.confirmDeleteUsers(event.itemsData);
      }
    },
    confirmDeleteUser(user) {
      const email = this.resolveUserEmail(user);
      const currentUserEmail = this.profileModule.profile?.email;
      if (currentUserEmail && email === currentUserEmail) {
        Swal.fire({
          title: "Nie można usunąć",
          html: "Nie możesz usunąć własnego konta.",
          icon: "warning",
          confirmButtonText: "OK",
          customClass: { container: "swal-highest" },
        });
        return;
      }
      const login = this.resolveUserLogin(user);
      Swal.fire({
        title: "Usuń użytkownika",
        html: `Czy na pewno chcesz usunąć użytkownika <strong>${login}</strong> (${email})?<br/><br/><span class="text-danger">Ta operacja jest nieodwracalna.</span>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj",
        confirmButtonColor: "#d33",
        customClass: { container: "swal-highest" },
      }).then((result) => {
        if (result.isConfirmed) {
          this.rbacModule.httpDeleteUserByEmail({ email }).then(() => {
            this.refreshUsersList();
          }).catch(() => {});
        }
      });
    },
    confirmDeleteUsers(usersData) {
      if (!usersData || usersData.length === 0) return;
      const currentUserEmail = this.profileModule.profile?.email;
      const filteredUsers = currentUserEmail
        ? usersData.filter((u) => this.resolveUserEmail(u) !== currentUserEmail)
        : usersData;
      const skippedSelf = filteredUsers.length < usersData.length;
      if (filteredUsers.length === 0) {
        Swal.fire({
          title: "Nie można usunąć",
          html: "Nie możesz usunąć własnego konta.",
          icon: "warning",
          confirmButtonText: "OK",
          customClass: { container: "swal-highest" },
        });
        return;
      }
      const skippedNote = skippedSelf
        ? `<br/><span class="text-warning">Twoje własne konto zostanie pominięte.</span>`
        : "";
      Swal.fire({
        title: "Usuń użytkowników",
        html: `Czy na pewno chcesz usunąć <strong>${filteredUsers.length}</strong> wybranych użytkowników?<br/><br/><span class="text-danger">Ta operacja jest nieodwracalna.</span>${skippedNote}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj",
        confirmButtonColor: "#d33",
        customClass: { container: "swal-highest" },
      }).then((result) => {
        if (result.isConfirmed) {
          const emails = filteredUsers.map((u) => this.resolveUserEmail(u));
          this.rbacModule.httpDeleteUsersByEmails({ emails }).then(() => {
            this.refreshUsersList();
          }).catch(() => {});
        }
      });
    },
    openRolesModal() {
      this.showRolesModal = true;
      this.loadRolesCatalog();
    },
    handleRolesUpdated(payload) {
      if (payload && payload.email) {
        this.userRolesMap[payload.email] = payload.roles || [];
        this.refreshTableData();
      }
    },
    handleUserCreated() {
      this.refreshUsersList();
    },
    viewUserProfile(user) {
      const email = user.email;
      if (!email) return;
      const escHtml = (str) => {
        if (!str) return "";
        return String(str)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };
      this.profileModule.httpFetchUserProfileByEmailForAdmin(email).then((profile) => {
        if (!profile) return;
        const avatarHtml =
          profile.avatarUrl && /^https?:\/\//.test(profile.avatarUrl)
            ? `<img src="${escHtml(profile.avatarUrl)}" alt="avatar" style="width:64px;height:64px;border-radius:50%;object-fit:cover;" class="mb-2"/><br/>`
            : "";
        const roles = Array.isArray(profile.roles)
          ? profile.roles.map((r) => `<span class="badge bg-secondary me-1">${escHtml(r)}</span>`).join("")
          : escHtml(profile.roles) || "-";
        const row = (label, value) =>
          `<tr><td class="text-muted pe-3" style="white-space:nowrap">${escHtml(label)}</td><td>${escHtml(value) || "-"}</td></tr>`;
        const html = `
          <div class="text-center">${avatarHtml}</div>
          <table class="table table-sm text-start">
            ${row("Email", profile.email)}
            ${row("Wyświetlana nazwa", profile.displayName)}
            ${row("Imię", profile.firstName)}
            ${row("Nazwisko", profile.lastName)}
            <tr><td class="text-muted pe-3" style="white-space:nowrap">Role</td><td>${roles || "-"}</td></tr>
            ${row("Telefon", profile.phone)}
            ${row("Tytuł", profile.title)}
            ${row("Dział", profile.department)}
            ${row("Notatka", profile.note)}
          </table>`;
        Swal.fire({
          title: "Profil użytkownika",
          html: html,
          icon: "info",
          confirmButtonText: "Zamknij",
          customClass: { container: "swal-highest" },
        });
      }).catch(() => {
        // error already handled by catchResponseError in the store action
      });
    },
    resolveUserEmail(user) {
      return user.email;
    },
    resolveUserLogin(user) {
      return user.login || user.username || user.userName || user.email || "-";
    },
    resolveUserDisplayName(user) {
      if (user.displayName) return user.displayName;
      if (user.fullName) return user.fullName;
      if (user.firstName || user.lastName) {
        return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-";
      }
      return user.login || user.username || "-";
    },
    extractRolesFromUser(user) {
      const email = this.resolveUserEmail(user);
      if (email && this.userRolesMap[email]) {
        return this.userRolesMap[email];
      }
      const roles = user.roles;
      const normalized = (Array.isArray(roles) ? roles : [roles])
        .map((role) => normalizeRoleName(role))
        .filter(Boolean);
      if (email && normalized.length) {
        this.userRolesMap[email] = normalized;
      }
      return normalized;
    },
    refreshTableData() {
      const currentUserEmail = this.profileModule.profile?.email;
      this.tableConfig.data = this.users.map((user) => {
        const userEmail = this.resolveUserEmail(user);
        const rolesList = this.userRolesMap[userEmail] || this.extractRolesFromUser(user);
        const isCurrentUser = currentUserEmail && userEmail === currentUserEmail;
        return  {
          ...user,
          id: userEmail,
          login: this.resolveUserLogin(user),
          email: user.email || "-",
          displayName: this.resolveUserDisplayName(user),
          roles: rolesList.length ? rolesList.join(", ") : "-",
          actions: [
            {
              icon: "fas fa-user-shield",
              action: "manage-roles",
              tooltip: "Zarządzaj rolami",
              enabled: this.canManageUsersAccess,
              class: "btn btn-sm btn-outline-primary",
            },
            {
              icon: "fas fa-id-card",
              action: "view-profile",
              tooltip: "Podgląd profilu",
              enabled: this.canManageUsersAccess,
              class: "btn btn-sm btn-outline-secondary",
            },
            {
              icon: "fas fa-trash-alt",
              action: "delete-user",
              tooltip: isCurrentUser ? "Nie możesz usunąć własnego konta" : "Usuń użytkownika",
              enabled: this.canManageUsersAccess && !isCurrentUser,
              class: "btn btn-sm btn-outline-danger",
            },
          ],
        };
      });

      console.log("Users to load to table:",this.tableConfig.data);
    },
    loadRolesCatalog() {
      if (this.rolesCatalog.length > 0) return;
      this.rbacModule.httpGetRoles().then(() => {
        this.rolesCatalog = [...this.rbacModule.getRolesCatalog];
      });
    },
    loadUserRoles(users) {
      console.log("Users to load roles:",users);
      const roleFetches = [];
      users.forEach((user) => {
        const userEmail = this.resolveUserEmail(user);
        if (!userEmail) return;
        const existingRoles = this.extractRolesFromUser(user);
        if (existingRoles.length) {
          this.userRolesMap[userEmail] = existingRoles;
          return;
        }
        roleFetches.push(
          this.rbacModule.httpGetUserRolesByEmail({ email: userEmail }).then((response) => {
            const payload = response?.data?.payload || response?.data || response?.payload || [];
            console.log("User roles:",payload);
            this.userRolesMap[userEmail] = normalizeRolesPayload(payload);
          })
        );
      });

      return Promise.all(roleFetches).then(() => {
        this.refreshTableData();
      });
    },
    refreshUsersList() {
      if (!this.canViewUsersAccess) {
        return;
      }
      this.rbacModule
        .httpGetUsersPaging({
          currentPage: this.currentPage,
          sortingKey: this.sortColumn,
          sortingDirection: this.sortingDirection,
          pageSize:this.pageSize
        })
        .then(() => {
          this.users = [...this.rbacModule.getUsers];
          console.log("Users:",this.users);
          this.totalPages = this.rbacModule.getUsersPagesCount || 1;
          this.totalRows = this.rbacModule.getUsersDataTotal || 0;
          return this.loadUserRoles(this.users);
        });
    },
  },
  mounted() {
    this.refreshUsersList();
  },
};
</script>
